import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";

export interface Device {
  id: number;
  name: string;
  type: string;
  storeId: number;
  changingRoomId?: number;
  updatedAt?: string;
  createdAt?: string;
  batteryPercent?: number;
  appVersion?: string;
  operatingSystemType?: string;
  deviceVersion?: string;
  model?: string;
  singleAppModeEnabled: boolean;
  enabled: boolean;
  allowedCamera: boolean;
  allowedMicrophone: boolean;
  allowedNotifications: boolean;
  environment: string;
  enableSingleAppMode: boolean;
  lastRestartTime: number;
  ignored?: boolean;
  metadata?: Record<string, string>;
}

export interface DeviceResponse<T> {
  success: boolean;
  body?: T;
  error?: Error;
}

export interface DeviceMetadataSnapshot {
  updatedAt: string;
  metadata: Record<string, string>;
}

const DEVICE_LOGS_BASE_URL = "https://app.mezmo.com/c30fde32f1/logs/view";

export const buildDevicesByStoreEndpoint = (storeId: number, includeDisabled: boolean): string => {
  const params = new URLSearchParams();
  params.set("includeDisabled", String(includeDisabled));

  return `/device/store/${storeId}?${params}`;
};

export const buildDeviceByIdEndpoint = (deviceId: number): string => `/device/${deviceId}`;

export const buildDeviceHistoryEndpoint = (deviceId: number, days: number): string => {
  const params = new URLSearchParams();
  params.set("days", String(days));

  return `/device/history/${deviceId}?${params}`;
};

export const buildDeviceMetadataEndpoint = (deviceId: number, days: number): string => {
  const params = new URLSearchParams();
  params.set("days", String(days));

  return `/device/history/${deviceId}/metadata?${params}`;
};

export const buildDeviceRestartEndpoint = (deviceId: number): string =>
  `/device/${deviceId}/remoteAction/restart`;

export const buildDeviceDisableEndpoint = (deviceId: number): string =>
  `/device/${deviceId}/disable`;

export const buildDeviceLockEndpoint = (deviceId: number, lock: boolean): string => {
  const params = new URLSearchParams();
  params.set("lock", String(lock));

  return `/device/${deviceId}/remoteAction/setLock?${params}`;
};

export const buildDevicePinEndpoint = (deviceId: number): string => `/admin/device/${deviceId}/pin`;

export const buildDeviceIgnoredEndpoint = (deviceId: number, ignored: boolean): string =>
  `/device/${deviceId}/ignored/${ignored}`;

export const buildDeviceLogsUrl = (deviceId: number, storeId: number): string => {
  const params = new URLSearchParams();
  params.set("q", `meta.device_id:${deviceId} meta.store_id:${storeId}`);

  return `${DEVICE_LOGS_BASE_URL}?${params}`;
};

export class DeviceService extends BaseApiService {
  private static instance: DeviceService;

  private constructor() {
    super();
  }

  public static getInstance(): DeviceService {
    if (!DeviceService.instance) {
      DeviceService.instance = new DeviceService();
    }
    return DeviceService.instance;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  private getAnalyticsBaseUrl(): string {
    return getApiConfig().analytics;
  }

  public async getDevicesByStoreId(
    storeId: number,
    includeDisabled: boolean = false,
  ): Promise<DeviceResponse<Device[]>> {
    try {
      const response = await this.get<{ data: Device[] }>(
        buildDevicesByStoreEndpoint(storeId, includeDisabled),
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        body: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  }

  public async getDeviceById(deviceId: number): Promise<DeviceResponse<Device>> {
    try {
      const response = await this.get<{ data: Device }>(
        buildDeviceByIdEndpoint(deviceId),
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        body: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  }

  public async getDevicePinById(deviceId: number): Promise<DeviceResponse<string>> {
    try {
      const response = await this.get<{ data: string }>(
        buildDevicePinEndpoint(deviceId),
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        body: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  }

  public async getDeviceHistoryById(
    deviceId: number,
    days: number,
  ): Promise<DeviceResponse<Device[]>> {
    try {
      const response = await this.get<{ data: Device[] }>(
        buildDeviceHistoryEndpoint(deviceId, days),
        this.getAnalyticsBaseUrl(),
      );
      return {
        success: true,
        body: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  }

  public async getLatestDeviceMetadataById(
    deviceId: number,
    days: number,
  ): Promise<DeviceResponse<DeviceMetadataSnapshot | null>> {
    try {
      const response = await this.get<{ data: DeviceMetadataSnapshot | null }>(
        buildDeviceMetadataEndpoint(deviceId, days),
        this.getAnalyticsBaseUrl(),
      );
      return {
        success: true,
        body: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  }

  public async setDeviceIgnoredById(
    deviceId: number,
    ignored: boolean,
  ): Promise<DeviceResponse<Device>> {
    try {
      const response = (await this.fetchWithAuth(
        buildDeviceIgnoredEndpoint(deviceId, ignored),
        { method: "PATCH" },
        this.getCoreBaseUrl(),
      )) as { data: Device };
      return {
        success: true,
        body: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  }

  public async disableDeviceById(deviceId: number): Promise<DeviceResponse<void>> {
    try {
      await this.fetchWithAuth(
        buildDeviceDisableEndpoint(deviceId),
        { method: "PUT" },
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  }

  public async lockDeviceById(deviceId: number, lock: boolean): Promise<DeviceResponse<void>> {
    try {
      await this.fetchWithAuth(
        buildDeviceLockEndpoint(deviceId, lock),
        { method: "POST" },
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  }

  public async restartDeviceById(deviceId: number): Promise<DeviceResponse<void>> {
    try {
      await this.fetchWithAuth(
        buildDeviceRestartEndpoint(deviceId),
        { method: "POST" },
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  }
}
